"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ANNUAL_DISCOUNT_PERCENT,
  getAnnualTotalForTier,
  PRICING_TIERS,
  type PublicPricingTierId,
} from "@/lib/pricing";

type BillingCycle = "monthly" | "annual";

const isPublicTier = (tierId: string): tierId is PublicPricingTierId =>
  tierId === "personal" || tierId === "plus" || tierId === "pro";

const formatUsd = (value: number) => `$${value.toFixed(2)}`;

export function PricingContent() {
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const cycleFromQuery = useMemo<BillingCycle>(
    () => (searchParams.get("cycle") === "annual" ? "annual" : "monthly"),
    [searchParams],
  );
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(cycleFromQuery);
  const checkoutStatus = useMemo(() => searchParams.get("checkout"), [searchParams]);

  useEffect(() => {
    setBillingCycle(cycleFromQuery);
  }, [cycleFromQuery]);

  const handleCheckout = async (tierId: PublicPricingTierId, cycle: BillingCycle) => {
    setLoadingTier(tierId);
    setError(null);

    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier: tierId, billingCycle: cycle, context: "pricing" }),
      });

      if (response.status === 401) {
        window.location.href = "/sign-in";
        return;
      }

      const payload = (await response.json()) as { url?: string; error?: string };

      if (!response.ok || !payload.url) {
        throw new Error(payload.error || "Unable to start checkout.");
      }

      window.location.href = payload.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout failed.");
      setLoadingTier(null);
    }
  };

  return (
    <div className="relative min-h-screen bg-background lg:h-[100dvh]">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1000px_circle_at_20%_-10%,hsl(210_100%_96%),transparent_60%),radial-gradient(900px_circle_at_80%_10%,hsl(45_100%_95%),transparent_55%)]" />
      <main className="no-scroll-desktop mx-auto flex min-h-screen max-w-6xl flex-col gap-8 px-4 py-10 sm:px-6 lg:h-full lg:min-h-0 lg:py-8">
        <header className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Pricing</p>
          <h1 className="text-3xl font-semibold text-foreground md:text-4xl">
            Choose the plan that fits your daily cadence.
          </h1>
          <p className="text-sm text-muted-foreground md:text-base">
            All 3 plans include a free trial and included AI usage. Add your payment method at
            checkout; billing starts automatically after 7 days unless you cancel.
          </p>
          {checkoutStatus === "cancel" ? (
            <p className="text-sm font-semibold text-amber-600">
              Checkout canceled. You can retry any time from billing.
            </p>
          ) : null}
          {error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}
        </header>

        <div className="flex justify-center">
          <div
            className="segmented-shell inline-flex gap-1 rounded-full p-1 text-sm font-medium"
            role="group"
            aria-label="Billing cycle"
          >
            {(["monthly", "annual"] as BillingCycle[]).map((cycle) => (
              <button
                key={cycle}
                type="button"
                onClick={() => setBillingCycle(cycle)}
                aria-pressed={billingCycle === cycle}
                className={`segmented-item inline-flex items-center gap-2 rounded-full px-5 py-2 transition ${
                  billingCycle === cycle
                    ? "segmented-item-active text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                {cycle === "monthly" ? "Monthly" : "Annual"}
                {cycle === "annual" ? (
                  <span className="stamp rounded-full px-2 py-0.5 text-[11px] font-semibold text-foreground/70">
                    Save {ANNUAL_DISCOUNT_PERCENT}%
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PRICING_TIERS.map((tier) => {
            const annualTotal = isPublicTier(tier.id) ? getAnnualTotalForTier(tier.id) : null;
            const annualMonthly = annualTotal ? Number((annualTotal / 12).toFixed(2)) : null;
            const displayPrice =
              billingCycle === "annual" && annualMonthly ? annualMonthly : tier.monthlyPrice;
            const billingNote =
              billingCycle === "annual" && annualTotal
                ? `Billed annually (${formatUsd(annualTotal)}/yr)`
                : "Billed monthly";

            return (
              <Card
                key={tier.id}
                className={
                  tier.featured
                    ? "border-primary/60 bg-card shadow-md"
                    : "border-border/60 bg-card/80"
                }
              >
                <CardHeader>
                  <CardTitle className="text-2xl">{tier.name}</CardTitle>
                  <CardDescription>{tier.tagline}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <p className="text-3xl font-semibold text-foreground">
                      {formatUsd(displayPrice)}
                      <span className="text-base font-normal text-muted-foreground">/mo</span>
                    </p>
                    <p className="text-xs font-medium text-muted-foreground">{billingNote}</p>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {tier.highlights.map((item) => (
                      <li key={item} className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary/70" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    variant={tier.featured ? "default" : "outline"}
                    disabled={loadingTier === tier.id || !isPublicTier(tier.id)}
                    onClick={() => {
                      if (!isPublicTier(tier.id)) return;
                      void handleCheckout(tier.id, billingCycle);
                    }}
                  >
                    {loadingTier === tier.id ? "Starting checkout..." : "Start free trial"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </section>
      </main>
    </div>
  );
}
